import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../api/useApiFetch'
import { useAuth } from './useAuth'

export type MembershipRole = 'SuperAdmin' | 'Admin' | 'User'

export interface Membership {
  districtId: string
  role: MembershipRole
}

export interface CurrentUserDto {
  id: string
  email: string
  displayName: string
  memberships: Membership[]
  impersonating: string | null
  // TODO(viltrapport-port phase 3): backend does not yet return activeSeasonId on this endpoint
  activeSeasonId?: string
}

const ROLE_RANK: Record<MembershipRole, number> = {
  SuperAdmin: 2,
  Admin: 1,
  User: 0,
}

// SuperAdmin satisfies every lower role; Admin satisfies Admin/User; User satisfies User.
export function hasRole(user: CurrentUserDto | undefined, role: MembershipRole): boolean {
  if (!user || !Array.isArray(user.memberships)) return false
  const required = ROLE_RANK[role]
  return user.memberships.some((m) => ROLE_RANK[m.role] >= required)
}

/** The user's role in a specific district, or undefined if not a member (or districtId is null). */
export function roleInDistrict(
  user: CurrentUserDto | undefined,
  districtId: string | null | undefined,
): MembershipRole | undefined {
  if (!user || !Array.isArray(user.memberships) || !districtId) return undefined
  return user.memberships.find((m) => m.districtId === districtId)?.role
}

/** Whether the user holds at least `required` in the given (active) district. */
export function hasRoleInDistrict(
  user: CurrentUserDto | undefined,
  districtId: string | null | undefined,
  required: MembershipRole,
): boolean {
  const role = roleInDistrict(user, districtId)
  if (!role) return false
  return ROLE_RANK[role] >= ROLE_RANK[required]
}

export interface CurrentUserOptions {
  /**
   * Read the account again once it is older than this, on mount and when the tab
   * regains focus. Unset, it is read once per session: it is cached for the session
   * and persisted across reloads.
   */
  refreshAfterMs?: number
}

export function useCurrentUser({ refreshAfterMs }: CurrentUserOptions = {}) {
  const { state } = useAuth()
  const call = useApiFetch()
  return useQuery({
    queryKey: ['authentication'],
    queryFn: () => call<CurrentUserDto>('/api/v1/authentication'),
    enabled: state.status === 'signed-in',
    staleTime: refreshAfterMs ?? Infinity,
    ...(refreshAfterMs !== undefined && { refetchOnMount: true, refetchOnWindowFocus: true }),
  })
}
